import { nextTick } from 'vue'

// Default theme colors
const DEFAULT_CONFETTI_COLORS = [
    '#6E3DEA', // Purple
    '#9A55F2', // Light purple
    '#00C7E4', // Cyan
    '#FDD67E', // Gold
    '#E63D87', // Pink
]

// Singleton instance to reuse across calls
let jsConfettiInstance = null

/**
 * Initialize the JSConfetti instance (lazy loading)
 * @returns {Promise<JSConfetti>}
 */
async function getConfettiInstance() {
    if (!jsConfettiInstance) {
        const JSConfetti = (await import('js-confetti')).default
        jsConfettiInstance = new JSConfetti()
    }
    return jsConfettiInstance
}

/**
 * Set z-index on confetti canvas elements to ensure they appear above modals/drawers
 * @param {number} zIndex - The z-index value to set (default: 10002)
 */
function setConfettiZIndex(zIndex = 10002) {
    nextTick(() => {
        setTimeout(() => {
            const canvases = document.querySelectorAll('body > canvas')
            if (canvases.length > 0) {
                canvases.forEach((canvas) => {
                    const style = window.getComputedStyle(canvas)
                    if (
                        style.position === 'fixed' ||
                        style.position === 'absolute'
                    ) {
                        canvas.style.zIndex = String(zIndex)
                        canvas.style.pointerEvents = 'none'
                    }
                })
            }
        }, 100)
    })
}

/**
 * Trigger confetti animation with customizable options
 * @param {Object} options - Configuration options
 * @param {string[]} options.confettiColors - Array of hex colors (default: theme colors)
 * @param {number} options.confettiRadius - Radius of confetti particles (default: 3)
 * @param {number} options.confettiNumber - Number of confetti particles (default: 150)
 * @param {Object} options.origin - Origin point {x, y} where 0-1 represents percentage (default: {x: 0.5, y: 0.9})
 * @param {number} options.zIndex - Z-index for confetti canvas (default: 10002)
 * @param {boolean} options.setZIndex - Whether to set z-index on canvas (default: true)
 * @returns {Promise<void>}
 */
export async function triggerConfetti(options = {}) {
    try {
        const {
            confettiColors = DEFAULT_CONFETTI_COLORS,
            confettiRadius = 3,
            confettiNumber = 150,
            origin = { x: 0.5, y: 0.9 },
            zIndex = 10002,
            setZIndex = true,
        } = options

        const instance = await getConfettiInstance()

        instance.addConfetti({
            confettiColors,
            confettiRadius,
            confettiNumber,
            origin,
        })

        if (setZIndex) {
            setConfettiZIndex(zIndex)
        }
    } catch (error) {
        console.error('Failed to trigger confetti:', error)
    }
}

/**
 * Convenience function for payment success confetti (larger, more particles)
 * @param {Object} options - Optional overrides
 * @returns {Promise<void>}
 */
export async function triggerPaymentSuccessConfetti(options = {}) {
    return triggerConfetti({
        confettiRadius: 4,
        confettiNumber: 200,
        ...options,
    })
}
